import { Injectable } from '@angular/core';

import { SpaContext } from '../../../../Classes/spacontext';
import { ApiResponse, VisibaApiHttp } from '../../../../Classes/visibaHttp.class';
import { TimeslotApiModel } from '../Models/TimeslotApiModel';
import { SearchCapabilitiesApiModel } from '../Models/SearchCapabilitiesApiModel';
import { AppointmentApiModel } from '../Models/AppointmentApiModel';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface SearchParameters {
	specialityId: string;
	conditionId: string;
	filterableUnitId: string;
	resourceId: string;
	date?: Date;
	fromDate?: Date;
	toDate?: Date;
	fromTime?: TimeSpan;
	toTime?: TimeSpan;
	physicalLocationId?: number;
	localityType?: LocalityType;
}

interface BookSlotApiInput {
	Reason: string;
	AsAgentFor: string;
	Files: Guid[];
	ExternalData: Guid[];
	ConditionId?: number;
}

export enum LocalityType {
	All = 'all',
	Digital = 'digital',
	Physical = 'physical',
}

interface AvailabilityModel {
	Supported: boolean;
	FirstAvailableUpcomingTime: string;
}

export interface LocalityAvailabilityModel {
	Digital: AvailabilityModel;
	PhysicalLocations: { [key: number]: AvailabilityModel };
}

@Injectable()
export class TimeslotServiceV2 {
	private baseUri = (): string => `/api/Client/v2/units/${this.spaContext.unitConfig.Unit.UnitId}/Timeslots`;

	constructor(
		private http: VisibaApiHttp,
		public spaContext: SpaContext,
	) {
		// Empty
	}

	public get(searchParams: SearchParameters, emitLoading = true): Observable<ApiResponse<TimeslotApiModel[]>> {
		let params = new HttpParams();

		Object.keys(searchParams).forEach((key) => {
			if (!searchParams[key]) return;
			params = params.append(key, searchParams[key]);
		});

		return this.http.getTyped<TimeslotApiModel[]>(this.baseUri(), true, { params }, emitLoading);
	}

	public getById(id: string): Observable<ApiResponse<TimeslotApiModel>> {
		return this.http.getTyped<TimeslotApiModel>(`${this.baseUri()}/${id}`);
	}

	public getSearchCapabilities(): Observable<ApiResponse<SearchCapabilitiesApiModel>> {
		return this.http.getTyped<SearchCapabilitiesApiModel>(`${this.baseUri()}/searchcapabilities`);
	}

	public getBookingById(id: string, bookingSlot: BookSlotApiInput): Observable<ApiResponse<TimeslotApiModel>> {
		return this.http.postTyped<TimeslotApiModel>(`${this.baseUri()}/${id}/book`, bookingSlot);
	}

	public book(
		composite: string,
		reason: string,
		files: Array<any>,
		externalDataReferences: Guid[] = [],
		conditionId: number = null,
		asAgentFor: string = null,
		redRobinSessionId: string | null,
		lockId: string,
	): Observable<ApiResponse<AppointmentApiModel>> {
		const sendModel = {
			Reason: reason,
			AsAgentFor: asAgentFor,
			Files: files,
			TimeslotLockId: lockId,
			ExternalData: externalDataReferences,
			ConditionId: conditionId,
			...(redRobinSessionId && { RedRobinSessionId: redRobinSessionId }),
		};

		return this.http.postTyped<AppointmentApiModel>(`${this.baseUri()}/${composite}/book`, sendModel);
	}

	public getLocalityAvailability(searchParams: SearchParameters): Observable<ApiResponse<LocalityAvailabilityModel>> {
		let params = new HttpParams();

		Object.keys(searchParams).forEach((key) => {
			if (!searchParams[key]) return;
			params = params.append(key, searchParams[key]);
		});

		return this.http.getTyped<LocalityAvailabilityModel>(`${this.baseUri()}/Availability`, true, { params });
	}
}
